import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../services/supabase'
import { useAuth } from './useAuth'
import {
  getPermissoes,
  setPermissoes,
  clearPermissoes,
  isSoftwareHouse
} from './authCache'

// Interface para permissão de menu
export interface PermissaoMenu {
  id: string
  empresa_id: string
  perfil: string
  menu_id: string
  submenu_id: string | null
  ativo: boolean
}

// Mapeamento de rotas para menu_id e submenu_id
const rotaParaMenu: Record<string, { menu_id: string; submenu_id: string | null }> = {
  '/': { menu_id: 'dashboard', submenu_id: null },
  '/estoque/produtos': { menu_id: 'estoque', submenu_id: 'produtos' },
  '/estoque/grupos': { menu_id: 'estoque', submenu_id: 'grupos' },
  '/estoque/subgrupos': { menu_id: 'estoque', submenu_id: 'subgrupos' },
  '/estoque/categorias': { menu_id: 'estoque', submenu_id: 'categorias' },
  '/estoque/marcas': { menu_id: 'estoque', submenu_id: 'marcas' },
  '/estoque/estoques': { menu_id: 'estoque', submenu_id: 'estoques' },
  '/estoque/transferencias': { menu_id: 'estoque', submenu_id: 'transferencias' },
  '/estoque/ajustes': { menu_id: 'estoque', submenu_id: 'ajustes' },
  '/estoque/extrato': { menu_id: 'estoque', submenu_id: 'extrato' },
  '/estoque/curva-abc': { menu_id: 'estoque', submenu_id: 'curva-abc' },
  '/estoque/produto-fornecedor': { menu_id: 'estoque', submenu_id: 'produto-fornecedor' },
  '/pessoas': { menu_id: 'pessoas', submenu_id: null },
  '/movimento/entrada': { menu_id: 'movimento', submenu_id: 'movimento-entrada' },
  '/movimento/saida': { menu_id: 'movimento', submenu_id: 'movimento-saida' },
  '/vendas/pdv': { menu_id: 'pdv', submenu_id: null },
  '/ordens-servico': { menu_id: 'servicos', submenu_id: 'ordens-servico' },
  '/contratos': { menu_id: 'contratos', submenu_id: 'contratos-lista' },
  '/contratos/modelos': { menu_id: 'contratos', submenu_id: 'contratos-modelos' },
  '/servicos': { menu_id: 'servicos', submenu_id: 'cadastro-servicos' },
  '/servicos/emitir-nfse': { menu_id: 'servicos', submenu_id: 'emitir-nfse' },
  '/financeiro/caixa-diario': { menu_id: 'financeiro', submenu_id: 'caixa-diario' },
  '/financeiro/contas-receber': { menu_id: 'financeiro', submenu_id: 'contas-receber' },
  '/financeiro/contas-pagar': { menu_id: 'financeiro', submenu_id: 'contas-pagar' },
  '/financeiro/fluxo-caixa': { menu_id: 'financeiro', submenu_id: 'fluxo-caixa' },
  '/financeiro/bancos': { menu_id: 'financeiro', submenu_id: 'bancos' },
  '/financeiro/plano-contas': { menu_id: 'financeiro', submenu_id: 'plano-contas' },
  '/financeiro/centros-custo': { menu_id: 'financeiro', submenu_id: 'centros-custo' },
  '/financeiro/dre': { menu_id: 'financeiro', submenu_id: 'dre' },
  '/financeiro/formas-pagamento': { menu_id: 'financeiro', submenu_id: 'formas-pagamento' },
  '/financeiro/carteira-cobranca': { menu_id: 'financeiro', submenu_id: 'carteira-cobranca' },
  '/documentos-eletronicos': { menu_id: 'movimento', submenu_id: 'movimento-entrada' },
  '/fiscal/codificacao': { menu_id: 'fiscal', submenu_id: 'codificacao-fiscal' },
  '/fiscal/codificacao-ncm': { menu_id: 'fiscal', submenu_id: 'codificacao-fiscal-ncm' },
  '/fiscal/tabela-icms': { menu_id: 'fiscal', submenu_id: 'tabela-icms' },
  '/fiscal/de-para': { menu_id: 'fiscal', submenu_id: 'fiscal-de-para' },
  '/fiscal/series': { menu_id: 'fiscal', submenu_id: 'series-fiscais' },
  '/notas-entrada': { menu_id: 'notas-entrada', submenu_id: null },
  '/ocorrencias': { menu_id: 'suporte', submenu_id: null },
  '/configuracoes/empresa': { menu_id: 'configuracoes', submenu_id: 'empresa' },
  '/configuracoes/usuarios': { menu_id: 'configuracoes', submenu_id: 'usuarios' },
  '/configuracoes/perfis-acesso': { menu_id: 'configuracoes', submenu_id: 'perfis-acesso' },
  '/configuracoes/integracoes': { menu_id: 'configuracoes', submenu_id: 'integracao' },
  '/configuracoes/terminais-caixa': { menu_id: 'configuracoes', submenu_id: 'terminais-caixa' },
  '/configuracoes/notificacoes-cobranca': { menu_id: 'configuracoes', submenu_id: 'notificacoes-cobranca' }
}

export interface UsePermissoesReturn {
  permissoes: PermissaoMenu[]
  loading: boolean
  canAccessMenu: (menuId: string, submenuId?: string | null) => boolean
  canAccessRoute: (path: string) => boolean
  refreshPermissoes: () => Promise<void>
}

export function usePermissoes(): UsePermissoesReturn {
  const { user, usuarioLogado, empresaId, loading: authLoading } = useAuth()
  const [permissoes, setPermissoesState] = useState<PermissaoMenu[]>([])
  const [loading, setLoading] = useState(true)

  // Buscar permissões do perfil do usuário
  const fetchPermissoes = useCallback(async () => {
    if (!usuarioLogado?.perfil || !empresaId) {
      setLoading(false)
      return
    }

    // Verificar cache primeiro
    const cachedPermissoes = getPermissoes()
    if (cachedPermissoes.length > 0) {
      setPermissoesState(cachedPermissoes)
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('perfis_permissoes_menu')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('perfil', usuarioLogado.perfil)

      if (error) {
        console.error('❌ Erro ao buscar permissões:', error)
        setLoading(false)
        return
      }

      const permissoesData = data || []
      setPermissoesState(permissoesData)
      setPermissoes(permissoesData)
    } catch (err) {
      console.error('❌ Erro ao buscar permissões:', err)
    } finally {
      setLoading(false)
    }
  }, [usuarioLogado?.perfil, empresaId])

  // Carregar permissões quando usuário estiver disponível.
  // Importante: NAO setar loading=false enquanto useAuth ainda esta carregando
  // OU enquanto existe um user autenticado mas usuarioLogado ainda nao chegou
  // (fetchUsuarioLogado e assincrono e nao bloqueia o useAuth.loading). Sem essa
  // guarda, deep links logo apos o login caiam em canAccessMenu com permissoes=[]
  // e o RouteGuard redirecionava pra / com toast falso de "sem permissao".
  useEffect(() => {
    if (usuarioLogado && empresaId) {
      fetchPermissoes()
    } else if (!authLoading && !user) {
      // Auth terminou e nao ha usuario autenticado — nao ha permissoes a buscar
      setLoading(false)
    }
    // Caso contrario: mantem loading=true ate usuarioLogado/empresaId chegarem
  }, [user, usuarioLogado, empresaId, authLoading, fetchPermissoes])

  // Verificar se pode acessar um menu específico
  const canAccessMenu = useCallback((menuId: string, submenuId?: string | null): boolean => {
    // Menu Suporte: acesso para Software House e CEO
    if (menuId === 'suporte') return isSoftwareHouse() || usuarioLogado?.perfil === 'CEO'

    // Software House tem acesso total
    if (isSoftwareHouse()) return true

    // CEO tem acesso total a tudo
    if (usuarioLogado?.perfil === 'CEO') return true

    // Dashboard e Pessoas sempre acessíveis para todos
    if (menuId === 'dashboard' || menuId === 'pessoas') return true

    // Se ainda está carregando, não mostrar nada (exceto dashboard)
    if (loading) return false

    // Se não tem permissões carregadas, bloquear por padrão
    if (permissoes.length === 0) return false

    // Buscar permissão específica
    const permissao = permissoes.find(p => {
      if (submenuId) {
        return p.menu_id === menuId && p.submenu_id === submenuId
      }
      return p.menu_id === menuId && p.submenu_id === null
    })

    return permissao?.ativo ?? false
  }, [permissoes, loading, usuarioLogado?.perfil])

  // Verificar se pode acessar uma rota específica
  const canAccessRoute = useCallback((path: string): boolean => {
    // Rota /ocorrencias: acesso para Software House e CEO
    if (path === '/ocorrencias') return isSoftwareHouse() || usuarioLogado?.perfil === 'CEO'

    // Software House tem acesso total
    if (isSoftwareHouse()) return true

    // CEO tem acesso total
    if (usuarioLogado?.perfil === 'CEO') return true

    // Dashboard sempre acessível para todos
    if (path === '/' || path === '') return true

    // Encontrar mapeamento de rota
    const mapeamento = rotaParaMenu[path]
    if (!mapeamento) {
      // Rota não mapeada — liberar com log para identificar rotas faltantes
      console.warn(`[PERMISSOES] Rota não mapeada em rotaParaMenu: ${path}`)
      return true
    }

    return canAccessMenu(mapeamento.menu_id, mapeamento.submenu_id)
  }, [canAccessMenu, usuarioLogado?.perfil])

  // Função para recarregar permissões
  const refreshPermissoes = useCallback(async () => {
    clearPermissoes()
    setLoading(true)
    await fetchPermissoes()
  }, [fetchPermissoes])

  return {
    permissoes,
    loading,
    canAccessMenu,
    canAccessRoute,
    refreshPermissoes
  }
}

// Exportar mapeamento de rotas para uso em outros lugares
export { rotaParaMenu }
